import { useCallback, useSyncExternalStore } from 'react';
import { createOperationDescriptor, getRequest, type Disposable } from 'relay-runtime';

import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';

import ViewPresenceBootstrapQuery from './__generated__/viewPresenceBootstrapQuery.graphql.ts';

import type { ContentHeaderPresenceContentSettingsQuery$data } from './presence/__generated__/ContentHeaderPresenceContentSettingsQuery.graphql.ts';
import ContentHeaderPresenceContentSettingsQuery from './presence/__generated__/ContentHeaderPresenceContentSettingsQuery.graphql.ts';
import { VIEW_PRESENCE_BOOTSTRAP_QUERY } from './viewPresenceBootstrap.ts';
import type {
	ViewPresenceBootstrapKey,
	ViewPresenceBootstrapResponse,
} from './viewPresenceBootstrap.ts';

// Browser-only first-use state; never hold a token in module scope or on the server.
let activeKey: string | undefined;
let consumed = false;
let tokenRetention: Disposable | undefined;
const releaseTokenRetention = () => {
	tokenRetention?.dispose();
	tokenRetention = undefined;
};
const inFlight = new Map<string, Promise<void>>();
const pendingPreloads = new Map<string, number>();
const pendingListeners = new Set<() => void>();

const keyFor = ({ cloudId, contentId, spaceKey }: ViewPresenceBootstrapKey) =>
	JSON.stringify([cloudId, contentId, spaceKey]);

const subscribeToPending = (listener: () => void) => {
	pendingListeners.add(listener);
	return () => pendingListeners.delete(listener);
};
const notifyPending = () => pendingListeners.forEach((listener) => listener());

/** Hold the settings reader until the route-owned preload (including its legacy fallback) settles. */
export function useViewPresencePreloadPending(variables: ViewPresenceBootstrapKey): boolean {
	const key = keyFor(variables);
	const getSnapshot = useCallback(() => pendingPreloads.has(key), [key]);
	return useSyncExternalStore(subscribeToPending, getSnapshot, () => false);
}

export async function trackViewPresencePreload<T>(
	variables: ViewPresenceBootstrapKey,
	preload: () => Promise<T>,
): Promise<T> {
	const key = keyFor(variables);
	if (isBrowser()) {
		pendingPreloads.set(key, (pendingPreloads.get(key) ?? 0) + 1);
		notifyPending();
	}
	try {
		return await preload();
	} finally {
		if (isBrowser()) {
			const remaining = (pendingPreloads.get(key) ?? 1) - 1;
			if (remaining > 0) pendingPreloads.set(key, remaining);
			else pendingPreloads.delete(key);
			notifyPending();
		}
	}
}

// The SSR preloader has a window shim. Never store request-specific state in this module on SSR.
const isBrowser = () =>
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr -- browser-only token handoff must exclude the SSR window shim
	!process.env.REACT_SSR && typeof window !== 'undefined';

// The route only invokes this preloader when both operations use Relay. Use the same
// environment as its query preloader and the header's existing settings reader.
const relayEnvironment = () => window.GLOBAL_RELAY_ENV;

const readCachedQuery = <T>(
	queryNode: Parameters<typeof getRequest>[0],
	variables: Record<string, string>,
): T | null => {
	const environment = relayEnvironment();
	if (!environment) return null;
	const operation = createOperationDescriptor(getRequest(queryNode), variables);
	const snapshot = environment.lookup(operation.fragment);
	return snapshot.isMissingData ? null : (snapshot.data as T | null);
};

/** Cache-only: neither the settings read nor a cache hit makes a network request. */
export function readCachedViewPresenceSetting(
	cloudId: string,
	spaceKey: string,
): boolean | undefined {
	const data = readCachedQuery<ContentHeaderPresenceContentSettingsQuery$data>(
		ContentHeaderPresenceContentSettingsQuery,
		{ cloudId, spaceKey },
	);
	const value = data?.confluence_teamPresenceContentSetting?.isEnabledOnContentView;
	return typeof value === 'boolean' ? value : undefined;
}

export async function preloadViewPresenceBootstrap(
	variables: ViewPresenceBootstrapKey,
): Promise<void> {
	const key = keyFor(variables);
	// Never share tokens or promises between server requests, including concurrent SSR.
	if (isBrowser()) {
		if (activeKey !== key) {
			releaseTokenRetention();
			activeKey = key;
			consumed = false;
		}
		if (inFlight.has(key)) return inFlight.get(key);
	}

	const preload = async () => {
		if (readCachedViewPresenceSetting(variables.cloudId, variables.spaceKey) !== undefined) {
			if (isBrowser() && activeKey === key) {
				consumed = true;
				releaseTokenRetention();
			}
			return;
		}
		// The caller normally guarantees a Relay environment. If one is unavailable,
		// avoid a redundant bootstrap request and let the legacy preloader recover.
		const environment = relayEnvironment();
		if (!environment) {
			const { preloadPresenceSettings } = await import('./preloadPresenceSettings.tsx');
			await preloadPresenceSettings(variables);
			if (isBrowser() && activeKey === key) consumed = true;
			return;
		}
		// Keep the result alive until the socket's first token callback; Relay can
		// collect an unretained preload before PagePresence mounts after paint.
		const retention =
			isBrowser() && !consumed
				? environment.retain(
						createOperationDescriptor(getRequest(ViewPresenceBootstrapQuery), variables),
					)
				: undefined;

		let response: { data: ViewPresenceBootstrapResponse | null; hasErrors: boolean } | undefined;
		try {
			response = await query<ViewPresenceBootstrapResponse, ViewPresenceBootstrapKey>({
				query: VIEW_PRESENCE_BOOTSTRAP_QUERY,
				variables,
				fetchPolicy: 'network-only',
			});
		} catch {
			// Network rejection takes the same legacy fallback as a GraphQL error.
		}
		const result = response?.data?.confluence_viewPresenceBootstrap;
		if (response?.hasErrors || !result || typeof result.isEnabledOnContentView !== 'boolean') {
			retention?.dispose();
			// An error must never be normalized to disabled. Keep the legacy setting/token path.
			const { preloadPresenceSettings } = await import('./preloadPresenceSettings.tsx');
			await preloadPresenceSettings(variables);
			if (isBrowser() && activeKey === key) consumed = true;
			return;
		}

		// A late response from a previous navigation must not overwrite the current setting.
		if (isBrowser() && activeKey !== key) {
			retention?.dispose();
			return;
		}
		const currentEnvironment = relayEnvironment();
		if (!currentEnvironment || currentEnvironment !== environment) {
			retention?.dispose();
			const { preloadPresenceSettings } = await import('./preloadPresenceSettings.tsx');
			await preloadPresenceSettings(variables);
			if (isBrowser() && activeKey === key) consumed = true;
			return;
		}
		environment.commitPayload(
			createOperationDescriptor(getRequest(ContentHeaderPresenceContentSettingsQuery), {
				cloudId: variables.cloudId,
				spaceKey: variables.spaceKey,
			}),
			{
				confluence_teamPresenceContentSetting: {
					isEnabledOnContentView: result.isEnabledOnContentView,
				},
			},
		);
		// The token stays in the content-specific bootstrap operation, never in the
		// space-scoped settings entry. A subsequent cache hit must not replay it.
		if (isBrowser() && activeKey === key && result.isEnabledOnContentView && !consumed) {
			tokenRetention = retention;
		} else {
			retention?.dispose();
			if (isBrowser() && activeKey === key) consumed = true;
		}
	};

	const promise = preload();
	if (isBrowser()) inFlight.set(key, promise);
	try {
		await promise;
	} finally {
		if (isBrowser() && inFlight.get(key) === promise) inFlight.delete(key);
	}
}

/** Only the browser can consume a hydrated result, once, for the exact content and space. */
export function takeInitialViewPresenceToken(
	variables: ViewPresenceBootstrapKey,
): { token: string | null } | undefined {
	if (!isBrowser()) return undefined;
	const key = keyFor(variables);
	// SSR hydration does not run the route preload in this browser process.
	if (activeKey === undefined) activeKey = key;
	if (activeKey !== key || consumed) return undefined;
	// Claim before reading: delayed stream responses cannot authenticate a reconnect.
	consumed = true;
	try {
		const data = readCachedQuery<ViewPresenceBootstrapResponse>(
			ViewPresenceBootstrapQuery,
			variables,
		);
		const result = data?.confluence_viewPresenceBootstrap;
		return result?.isEnabledOnContentView === true ? { token: result.token } : undefined;
	} finally {
		releaseTokenRetention();
	}
}
